import React from "react";
import styled from "styled-components";
import Spinner from "react-spinkit";
import { recordEvent } from "settings/socketDebug";
import { requestSubscriptionRebuild } from "settings/apolloClient.jsx";

// A render that throws anywhere below this boundary would otherwise unmount the
// whole React tree, leaving a blank white page. A reload does not fix it while
// the condition that caused the throw persists (a flapping socket feeding a
// panel undefined data, for instance, keeps throwing on the next mount), so the
// operator has to close the browser tab to recover. That is the "tela em branco,
// F5 não resolve, só fechar a guia" report.
//
// This boundary turns that dead end into a recoverable state:
//   1. it shows a fallback instead of the white void;
//   2. it records the crash into the socket history (`__socket.history()`) so the
//      next occurrence on the bench leaves evidence instead of vanishing with the
//      closed tab;
//   3. it rebuilds the subscription client once — a fresh socket re-joins and
//      re-subscribes, the same recovery closing and reopening the tab achieves —
//      and then retries the render.
//
// A circuit breaker is the important part: recovery fires only from an actually
// caught exception, at most once per window, and a second crash inside that
// window stops the auto-retry and shows a manual reload. A previous timer-based
// attempt that polled socket state and rebuilt on an ambiguous reading caused a
// reload loop (the panel flickered every 15s); reacting to real throws with a
// hard cap cannot flicker in a healthy session.
const RECOVERY_SETTLE_MS = 3000;
const RECOVERY_WINDOW_MS = 20000;
const MAX_AUTO_RECOVERIES = 1;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: ${props => (props.compact ? "24px" : "48px")};
  min-height: ${props => (props.compact ? "160px" : "60vh")};
  text-align: center;
  color: #66615b;
`;

const Message = styled.p`
  margin: 0;
  font-size: ${props => (props.compact ? "14px" : "16px")};
  max-width: 420px;
`;

const ReloadButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  background-color: #51cbce;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #34b5b8;
  }
`;

class ErrorBoundary extends React.Component {
  state = {
    hasError: false,
    recovering: false,
    gaveUp: false,
  };

  recoveryCount = 0;

  windowStartedAt = 0;

  recoveryTimer = null;

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const { label } = this.props;
    const message = error && error.message ? error.message : String(error);
    const where = label ? `[${label}] ` : "";

    // Lands in the same rolling buffer as the socket events, so `__socket.history()`
    // shows the crash next to the reconnects that led to it.
    recordEvent("render crashed", `${where}${message}`);

    // eslint-disable-next-line no-console
    console.error("[error-boundary]", where, error, info && info.componentStack);

    const now = Date.now();
    if (now - this.windowStartedAt > RECOVERY_WINDOW_MS) {
      this.windowStartedAt = now;
      this.recoveryCount = 0;
    }

    if (this.recoveryCount >= MAX_AUTO_RECOVERIES) {
      recordEvent("render crash unrecoverable", `${where}deu up após ${this.recoveryCount} tentativas`);
      this.setState({ recovering: false, gaveUp: true });
      return;
    }

    this.recoveryCount += 1;
    this.setState({ recovering: true });

    // Rebuild the subscription client (fresh socket) before retrying the render:
    // the crash is usually downstream of a dead/flapping socket, and remounting
    // the same panels against the same dead socket would just crash again.
    try {
      requestSubscriptionRebuild();
    } catch (rebuildError) {
      // no-op: recovery is best-effort; the retry below still runs
    }

    this.recoveryTimer = setTimeout(() => {
      this.recoveryTimer = null;
      this.setState({ hasError: false, recovering: false });
    }, RECOVERY_SETTLE_MS);
  }

  componentWillUnmount() {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
  }

  render() {
    const { children, compact } = this.props;
    const { hasError, recovering, gaveUp } = this.state;

    if (!hasError) return children;

    if (gaveUp) {
      return (
        <Container compact={compact}>
          <Message compact={compact}>
            Não foi possível recuperar o painel automaticamente. Recarregue a página.
          </Message>
          <ReloadButton type="button" onClick={() => window.location.reload()}>
            Recarregar
          </ReloadButton>
        </Container>
      );
    }

    return (
      <Container compact={compact}>
        <Spinner name="line-spin-fade-loader" color="green" />
        <Message compact={compact}>
          {recovering
            ? "Reconectando com o controlador…"
            : "Ocorreu um erro ao atualizar o painel."}
        </Message>
      </Container>
    );
  }
}

export default ErrorBoundary;
